import { useMemo } from 'react';
import { ChevronLeft, ChevronRight, History } from 'lucide-react';
import { CollapsiblePanel } from '@/components/CollapsiblePanel';
import { Box } from '@/components/dashboard/DashboardParts';
import { ListStatus } from '@/components/list/ListStatus';
import type { Sort } from '@/components/table/sort';
import { useListControls } from '@/hooks/useListControls';
import { usePagedList } from '@/hooks/usePagedList';
import { StatusHistoryTable, type StatusHistoryLine } from './StatusHistoryTable';

/** 5 lignes par page : une feuille en a souvent 4 à 6 (brouillon, soumise, en attente, validée, un refus) */
const PAGE_SIZE = 5;

const DEFAULT_SORT: Sort<'createdAt'> = { key: 'createdAt', direction: 'desc' };

/**
 * Bloc « Historique du statut » d'un objet, paginé côté API (GET /…_status_histories?<filtres>&order[createdAt]=…
 * &page=…&itemsPerPage=5) : la plus récente en premier, tri par date, 5 par page ; pagination compacte (‹ 1–5 sur 7 ›,
 * le bloc est souvent étroit), masquée quand tout tient sur une page. version : la changer recharge (après une action qui ajoute une ligne : soumission, signature, refus).
 * collapsed : bloc repliable, replié à l'arrivée et chargé à la première ouverture (écrans où il est secondaire, sur mobile).
 */
export function PagedStatusHistory<T extends ModelConstructor>({
  className,
  collapsed = false,
  ...props
}: HistoryProps<T> & {
  className?: string;
  /** Replié à l'arrivée, chargé à la première ouverture */
  collapsed?: boolean;
}) {
  if (collapsed) {
    return (
      <CollapsiblePanel title={TITLE} defaultCollapsed lazy data-testid="status-history-panel">
        <div className="px-6 py-2">
          <HistoryContent {...props} />
        </div>
      </CollapsiblePanel>
    );
  }
  return (
    <Box title={TITLE} icon={History} className={className} empty={false}>
      <HistoryContent {...props} />
    </Box>
  );
}

const TITLE = 'Historique du statut';

interface HistoryProps<T extends ModelConstructor> {
  modelClass: T;
  /** Filtres de l'objet (ex. { timeSheet: 12 }) */
  filters: Record<string, string | number>;
  toLine: (model: InstanceType<T>) => StatusHistoryLine;
  version?: number;
  /** Message sans aucune ligne */
  empty: string;
  testId?: string;
}

/** Lignes chargées (page, tri), message sans ligne et pagination ; dans le cadre choisi par PagedStatusHistory */
function HistoryContent<T extends ModelConstructor>({
  modelClass,
  filters,
  toLine,
  version = 0,
  empty,
  testId,
}: HistoryProps<T>) {
  const { sort, page, itemsPerPage, changeSort, pagination } = useListControls({
    filters: {},
    sort: DEFAULT_SORT,
    pageSizes: [PAGE_SIZE],
  });
  const filtersKey = JSON.stringify(filters);
  const request = useMemo(
    () => ({
      filters: JSON.parse(filtersKey) as Record<string, string | number>,
      // Même date (une transition et celle qu'elle déclenche) : dans l'ordre d'enregistrement
      order: { createdAt: sort.direction, id: sort.direction },
      page,
      itemsPerPage,
    }),
    [filtersKey, sort, page, itemsPerPage],
  );
  const { models, totalItems, loading, error } = usePagedList(modelClass, request, version);

  if (!loading && !error && totalItems === 0) return <p className="py-3 text-sm text-slate-500">{empty}</p>;

  return (
    <>
      {error || (loading && !models.length) ? (
        <ListStatus className="py-3" loading={loading} error={error} />
      ) : (
        <>
          <StatusHistoryTable lines={models.map(toLine)} sort={sort} onSort={changeSort} testId={testId} />
          {totalItems > itemsPerPage && (
            <div
              className="flex items-center justify-end gap-1 py-2 text-sm text-slate-600 border-t border-slate-100"
              data-testid="history-pager"
            >
              <button
                type="button"
                aria-label="Changements plus récents"
                disabled={page === 1}
                onClick={() => pagination.onPageChange(page - 1)}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="tabular-nums">
                {(page - 1) * itemsPerPage + 1}–{Math.min(page * itemsPerPage, totalItems)} sur {totalItems}
              </span>
              <button
                type="button"
                aria-label="Changements plus anciens"
                disabled={page * itemsPerPage >= totalItems}
                onClick={() => pagination.onPageChange(page + 1)}
                className="p-1 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}
